<template>
  <fragment v-if="campaign">
    <div v-if="campaign.IsAutoCreated" class="autocreated">
      {{ $t("CampaignEdit.IsAutoCreated") }}
    </div>
    <div :class="{ disabled: campaign.IsAutoCreated }">
      <div v-if="campaignEditFacebookData.Campaigns.length === 0" class="caption">
        {{ $t("CampaignEdit.Google.NoCampaignsCaption") }}
      </div>
      <div v-if="campaignEditFacebookData.Campaigns.length > 0" class="connected-campaigns">
        <div class="caption">
          {{ $t("CampaignEdit.Google.ConnectedCampaigns") }}
        </div>
        <ul>
          <li v-for="campaign in campaignEditFacebookData.Campaigns" :key="campaign">
            {{ campaign }}
          </li>
        </ul>
      </div>
      <div v-if="campaignEditFacebookData.AdSets.length > 0" class="connected-adgroups">
        <div class="caption">
          {{ $t("CampaignEdit.Google.ConnectedAdGroups") }}
        </div>
        <ul>
          <li v-for="adGroup in campaignEditFacebookData.AdSets" :key="adGroup">
            {{ adGroup }}
          </li>
        </ul>
      </div>
      <div v-if="campaignEditFacebookData.Campaigns.length > 0">
        <div class="token-message">{{ checkTokenMessage }}</div>
        <div class="buttons">
          <b-button :label="$t('CampaignEdit.Facebook.CheckToken')" v-if="!checkFacebookTokenData.hasPermissions" @click="checkToken" />
          <b-button :label="$t('CampaignEdit.Google.RepairToken')" v-if="!checkFacebookTokenData.isValid" @click="repairToken" />
          <b-button :label="$t('CampaignEdit.Facebook.Disconnect')" type="is-primary" @click="clearTemplate" />
        </div>
      </div>
      <div v-else>
        <p v-html="$t('CampaignEdit.Facebook.NoCampaignsText')"></p>
        <b-button :label="$t('CampaignEdit.Facebook.Connect')" type="is-primary" class="set-template-btn" @click="openFacebook" />
      </div>
      <div v-if="campaignEditFacebookData.Campaigns.length === 0">
        <div class="caption">{{ $t("CampaignEdit.Google.SendMessageCaption") }}</div>
        <div class="channel-edit-group channel-edit-group-fields">
          <div class="field-w">
            <b-field :label="$t('CampaignEdit.Google.SendMessageEmail')">
              <b-input v-model="sendMessageData.email" :placeholder="$t('CampaignEdit.Google.SendMessageEmail')" />
            </b-field>
          </div>
          <div class="field-w">
            <b-field :label="$t('CampaignEdit.Google.SendMessageText')">
              <b-input type="textarea" rows="3" v-model="sendMessageData.message" :placeholder="$t('CampaignEdit.Google.SendMessageText')" />
            </b-field>
          </div>
          <div class="field-w" v-if="!messageSent">
            <b-button :label="$t('Send')" type="is-primary" @click="sendMessage" />
          </div>
        </div>
        <div class="message-sent" v-if="messageSent">{{ $t("CampaignEdit.Google.MessageSent") }}</div>
      </div>
    </div>  </fragment>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import Fragment from "vue-fragment";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/he";
import { Getter } from "vuex-class";
import i18n from "@/i18n";
import { Result } from "@/models/Result";
import { Campaign, CampaignEditViewModel } from "@/models/campaigns/Campaign";
import { CampaignEditFacebookData } from "@/models/campaigns/CampaignEditFacebookData";
import { CampaignEditPageKind } from "@/models/campaigns/CampaignEditPageKind";
import { CheckChannelCampaignsData } from "@/models/campaigns/CheckChannelCampaignsData";
import { CheckFacebookTokenData } from "@/models/campaigns/CheckFacebookTokenData";
import CampaignService from "@/services/CampaignService";
import CampaignsApiService from "@/services/api/CampaignsApiService";
import FacebookApiService from "@/services/api/FacebookApiService";
import HelperService from "@/services/HelperService";

Vue.use(Fragment.Plugin);
const namespace = "campaignsModule";

@Component({
  components: {
  },
})
export default class TabFacebook extends Vue {

  //#region Vuex getters and actions

  @Getter("campaignEditViewModel", { namespace }) campaignEditViewModel!: CampaignEditViewModel;

  //#endregion Vuex getters and actions


  //#region Properties

  checkTokenMessage = "";
  messageSent = false;
  campaignEditFacebookData: CampaignEditFacebookData = {
    ConnectUrl: "",
    IsAutoCreated: false,
    MsgToProgrammer: "",
    AdSets: [],
    Campaigns: [],
  };
  checkFacebookTokenData: CheckFacebookTokenData = {
    hasPermissions: true,
    isValid: true,
  };
  sendMessageData: SendMessageData = {
    email: "",
    message: "",
  }

  CampaignEditPageKind = CampaignEditPageKind;

  //#endregion Properties


  //#region Watchers

  @Watch("campaignEditViewModel", { immediate: true })
  onCampaignEditViewModelChange(newCampaignEditViewModel: CampaignEditViewModel, oldCampaignEditViewModel: CampaignEditViewModel): void {
    if (!newCampaignEditViewModel) {
      return;
    }
    this.loadData(false);
  }

  //#endregion Watchers


  //#region Getters

  get campaign(): Campaign {
    return this.campaignEditViewModel?.Campaign;
  }

  get pageKind(): CampaignEditPageKind {
    return CampaignService.getCampaignEditPageKind(this.$route.path);
  }

  //#endregion Getters


  //#region Handlers

  checkToken() {
    this.loadData(true);
  }

  clearTemplate() {
    const loader = this.$loading.show();
    FacebookApiService.clearTemplateFacebook(this.campaign.ID).then((result: Result) => {
      if (result.Success) {
        this.loadData(false);
        HelperService.showMessage("", i18n.t("CampaignEdit.Facebook.CampaignDeactivated").toString());
      } else {
        HelperService.showError(result.Message);
      }
    }).finally(() => {
      loader.hide();
    });
  }

  openFacebook() {
    const loader = this.$loading.show();
    FacebookApiService.connectCampaign(this.campaign.ID).then((result: Result) => {
      if (result.Success) {
        const data = result.Data as CheckChannelCampaignsData;
        if (data.campaignCount > 0) {
          this.loadData(false);
        }
      } else {
        HelperService.showError(result.Message);
      }
    }).finally(() => {
      loader.hide();
    });
  }

  repairToken() {
    FacebookApiService.repairToken(this.campaign.ID);
  }

  sendMessage() {
    if (!this.sendMessageData.email) {
      HelperService.showError(i18n.t("EmailRequired").toString());
      return;
    }
    const loader = this.$loading.show();
    CampaignsApiService.sendMessage(this.sendMessageData.email, this.sendMessageData.message).then((result: Result) => {
      if (result.Success) {
        this.messageSent = true;
      } else {
        HelperService.showError(result.Message);
      }
    }).finally(() => {
      loader.hide();
    });
  }
  
  //#endregion Handlers


  //#region Private methods

  loadData(manual: boolean) {
    const loader = this.$loading.show();
    CampaignsApiService.getCampaignEditFacebookData(this.campaign).then((data: CampaignEditFacebookData) => {
      this.campaignEditFacebookData = data;
      this.sendMessageData.message = data.MsgToProgrammer;
      if (data.Campaigns.length > 0) {
        FacebookApiService.checkToken(this.campaign.ID, manual).then((result: Result) => {
          if (result.Success) {
            this.checkFacebookTokenData = result.Data as CheckFacebookTokenData;
            if (this.checkFacebookTokenData.isValid) {
              this.checkTokenMessage = i18n.t("CampaignEdit.Google.TokenIsValid").toString();
              if (!this.checkFacebookTokenData.hasPermissions) {
                this.checkTokenMessage = i18n.t("CampaignEdit.Facebook.PermissionError").toString() + this.checkTokenMessage;
              }
            } else {
              this.checkTokenMessage = i18n.t("CampaignEdit.Google.TokenIsNotValid").toString();
              this.checkTokenMessage += ` ${result.Message}`;
            }
          } else {
            HelperService.showError(result.Message);
          }
        }).finally(() => {
          loader.hide();
        });
      } else {
        loader.hide();
      }
    });
  }

  //#endregion Private methods
}

interface SendMessageData {
  email: string;
  message: string;
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.autocreated {
  margin-bottom: 1rem;
}

.buttons {
  button {
    margin-block-end: 1rem;
  }
}

.caption {
  font-weight: bold;
  margin-bottom: 1rem;
}

.connected-adgroups, .connected-campaigns {
  ul {
    list-style: disc;
    margin-bottom: 1rem;

    li {
      margin-left: 1.2rem;
    }
  }
}

.disabled {
  opacity: 0.7;
  pointer-events: none;
}

.channel-edit-group {
  margin-bottom: 25px;

  &.channel-edit-group-field {
    max-width: 894px;

    .channel-edit-field {
      margin-bottom: 1rem;
    }

    .label {
      font-size: 17px;
      margin: 0;
      display: inline;
    }
  }

  &.channel-edit-group-fields {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -12px;

    .field-w {
      padding: 0 12px 25px;
      flex-grow: 1;
      width: 100%;
    }
  }
}

.message-sent {
  color: $success;
}

.set-template-btn {
  margin: 1rem 0;
}

.token-message {
  margin-bottom: 1rem;
}
</style>
